import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { jobAPI, applicationAPI } from '../../services/api';
import {
    FaArrowLeft, FaBriefcase, FaUser, FaEnvelope,
    FaFileAlt, FaCheckCircle, FaTimesCircle, FaClock,
    FaEye, FaDownload
} from 'react-icons/fa';
import {
    DashboardHeader,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const ManageJobApplications = () => {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadJobAndApplications();
    }, [id]);

    const loadJobAndApplications = async () => {
        try {
            setLoading(true);
            const [jobRes, appsRes] = await Promise.all([
                jobAPI.getById(id),
                jobAPI.getApplications(id)
            ]);

            if (jobRes.success) setJob(jobRes.job);
            if (appsRes.success) setApplications(appsRes.applications || []);
        } catch (err) {
            console.error('Failed to load job data:', err);
            toast.error('Failed to load job applications');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (applicationId, newStatus) => {
        try {
            await jobAPI.updateApplicationStatus(applicationId, newStatus);
            toast.success(`Application marked as ${newStatus}`);
            loadJobAndApplications();
        } catch (err) {
            console.error('Failed to update status:', err);
            toast.error('Failed to update application status');
        }
    };

    if (loading) return <LoadingSpinner />;

    if (!job) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-8">
                <EmptyState message="Job not found" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=jobs" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                    <FaArrowLeft className="text-sm" />
                    <span className="font-medium">Back to Jobs</span>
                </Link>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                    <div className="flex justify-between items-start mb-4">
                        <DashboardHeader
                            icon={FaBriefcase}
                            title={job.title}
                            subtitle={`${job.location} • ${job.job_type.replace('_', ' ')}`}
                        />
                        <StatusBadge status={job.status} />
                    </div>
                    <div className="flex gap-4 text-sm text-gray-600">
                        <p>Posted on: {new Date(job.created_at).toLocaleDateString()}</p>
                        <p>Deadline: {job.application_deadline ? new Date(job.application_deadline).toLocaleDateString() : 'None'}</p>
                        <p>Applications: {applications.length}</p>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h2 className="text-xl font-bold text-gray-900">Applicants</h2>
                    </div>

                    {applications.length === 0 ? (
                        <div className="p-12 text-center">
                            <EmptyState
                                icon={FaUser}
                                message="No applications received yet for this job."
                            />
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applicant</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied Date</th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {applications.map((app) => (
                                        <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {app.first_name} {app.surname}
                                                        </div>
                                                        <div className="text-sm text-gray-500">{app.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge status={app.status || 'pending'} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(app.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex gap-2">
                                                    {app.resume_path && (
                                                        <a
                                                            href={`${(import.meta.env.VITE_API_URL || 'http://localhost:5000').replace('/api', '')}/uploads/${app.resume_path}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                                            title="View Resume"
                                                        >
                                                            <FaEye />
                                                        </a>
                                                    )}
                                                    {app.status === 'pending' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleStatusUpdate(app.id, 'reviewed')}
                                                                className="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                                                                title="Mark as Reviewed"
                                                            >
                                                                <FaClock />
                                                            </button>
                                                            <button
                                                                onClick={() => handleStatusUpdate(app.id, 'shortlisted')}
                                                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                                                title="Shortlist"
                                                            >
                                                                <FaCheckCircle />
                                                            </button>
                                                            <button
                                                                onClick={() => handleStatusUpdate(app.id, 'rejected')}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                                title="Reject"
                                                            >
                                                                <FaTimesCircle />
                                                            </button>
                                                        </>
                                                    )}
                                                    {app.status === 'reviewed' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleStatusUpdate(app.id, 'shortlisted')}
                                                                className="p-2 text-green-600 hover:bg-green-50 rounded"
                                                                title="Shortlist"
                                                            >
                                                                <FaCheckCircle />
                                                            </button>
                                                            <button
                                                                onClick={() => handleStatusUpdate(app.id, 'rejected')}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                                                title="Reject"
                                                            >
                                                                <FaTimesCircle />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageJobApplications;
